// Per-org entry templates (UX phase 3). A template is a reusable "New from template" skeleton:
// an item type + its field structure + tags + non-secret default values. It is stored per org as
// client-encrypted ciphertext (enc_name + enc_meta) exactly like device-node metadata (§19) — the
// server is zero-knowledge about template contents. Secret field VALUES are never written into a
// template; only the field structure (key/label/type/secret flag) and non-secret defaults survive.
import { api, type Template } from "../api/client";
import { b64decode, b64encode, encryptName, decryptName, encryptMeta, decryptMeta } from "../crypto/vault";
import type { DecryptedItem, Field, ItemContent } from "./items";
import { templateFor } from "./catalog";

// Field types whose values are sensitive and must be cleared when an item becomes a template.
const SECRET_TYPES = new Set([
  "secret", "password", "passphrase", "psk", "totp", "otp_recovery_codes",
  "ssh_private_key", "private_key", "connection_string", "certificate",
]);
function isSecret(f: Field): boolean {
  return f.secret === true || SECRET_TYPES.has(f.type);
}

/** The encrypted skeleton — everything needed to seed a new item, minus secrets. */
export interface TemplateMeta {
  type: string;
  tags?: string[];
  notes?: string;
  fields: Field[]; // secret values blanked; non-secret defaults kept
}

export interface VaultTemplate {
  id: string;
  name: string;
  meta: TemplateMeta;
}

/** Build a template skeleton from an existing item, stripping every secret value. */
export function skeletonFromItem(item: DecryptedItem): { name: string; meta: TemplateMeta } {
  const fields = item.content.fields.map((f) => ({ ...f, value: isSecret(f) ? "" : f.value, note: undefined }));
  return {
    name: item.content.name || templateFor(item.type).label,
    meta: { type: item.type, tags: item.content.tags ?? [], notes: item.content.notes ?? "", fields },
  };
}

/** Seed a fresh editor content from a template (a deep copy so edits don't mutate the store). */
export function contentFromTemplate(tmpl: VaultTemplate): { type: string; content: ItemContent } {
  return {
    type: tmpl.meta.type,
    content: {
      name: "",
      tags: [...(tmpl.meta.tags ?? [])],
      notes: tmpl.meta.notes ?? "",
      fields: tmpl.meta.fields.map((f) => ({ ...f })),
    },
  };
}

function decode(orgKey: Uint8Array, t: Template): VaultTemplate {
  return {
    id: t.id,
    name: decryptName(orgKey, b64decode(t.enc_name)),
    meta: decryptMeta<TemplateMeta>(orgKey, b64decode(t.enc_meta)),
  };
}

export async function loadTemplates(orgId: string, orgKey: Uint8Array): Promise<VaultTemplate[]> {
  const { templates } = await api.listTemplates(orgId);
  const out: VaultTemplate[] = [];
  for (const t of templates) {
    try {
      out.push(decode(orgKey, t));
    } catch {
      // A single undecryptable template must not break the list.
    }
  }
  return out;
}

export async function saveTemplate(orgId: string, orgKey: Uint8Array, name: string, meta: TemplateMeta): Promise<VaultTemplate> {
  const t = await api.createTemplate(orgId, b64encode(encryptName(orgKey, name)), b64encode(encryptMeta(orgKey, meta)));
  return decode(orgKey, t);
}

export async function deleteTemplate(id: string): Promise<void> {
  await api.deleteTemplate(id);
}
